import { useEffect, useReducer, useContext } from 'react'
import { NotesFieldsArrayEdit } from '../../../../types/form-types'
import '../../../../styles/4-layout/_note-container.scss'
import Note from './note'
import NoteEdit from './note-edit'
import { getRandomID } from '../../../services/form-utils'
import { UserContext } from '../../../state/user-state'
import { createToastMessage, Heading, ToastMessage } from '@msaf/core-react'

interface NotesWrapperProps {
  notes: Partial<NotesFieldsArrayEdit>
  isLoading: boolean
  addField: (note: object) => void
  removeField: (index: number) => void
  editField: (note: object, idx: number) => void
  setIsDirty: (isDirty: boolean) => void
}

function noteReducer(state: any, action: any) {
  switch (action.type) {
    case 'field': {
      return {
        ...state,
        [action.fieldName]: action.payload,
        createdTimestamp: new Date().toISOString(),
      }
    }
    case 'edit': {
      return {
        id: action.payload.note.id,
        note: action.payload.note.note,
        isPublic: action.payload.note.isPublic,
        createdTimestamp: action.payload.note.createdTimestamp,
        createdBy: `${action.payload.user.firstName ?? ''} ${action.payload.user.lastName ?? ''}`,
        idx: action.payload.idx,
        isEditing: true,
        isDirty: true,
      }
    }
    case 'clear': {
      return {
        id: getRandomID(),
        note: '',
        isPublic: false,
        createdTimestamp: new Date().toISOString(),
        createdBy: `${action.payload.firstName ?? ''} ${action.payload.lastName ?? ''}`,
        idx: -1,
        isEditing: false,
        isDirty: false,
        isInvalid: false,
      }
    }
    default:
      return state
  }
}

export default function NotesWrapperEdit({
  notes,
  isLoading,
  addField,
  removeField,
  editField,
  setIsDirty,
}: NotesWrapperProps) {
  const { state: user } = useContext(UserContext)
  const initialState = {
    id: getRandomID(),
    note: '',
    isPublic: false,
    createdTimestamp: new Date().toISOString(),
    createdBy: `${user.firstName ?? ''} ${user.lastName ?? ''}`,
    idx: -1,
    isEditing: false,
    isDirty: false,
    isInvalid: false,
  }

  const [state, dispatch] = useReducer(noteReducer, initialState)
  const { id, note, isPublic, createdTimestamp, createdBy, idx, isEditing, isDirty, isInvalid } = state

  const newNote = {
    id,
    note,
    isPublic,
    createdTimestamp,
    createdBy,
    isEditable: true,
  }

  const cancelHandler = () => {
    dispatch({ type: 'clear', payload: user })
  }

  // toggle isPublic in note object
  const togglePublicHandler = () => {
    dispatch({ type: 'field', fieldName: 'isPublic', payload: !isPublic })
  }

  // set note content
  const setNoteContentHandler = (value: string) => {
    dispatch({ type: 'field', fieldName: 'note', payload: value })
    setIsDirty(true)
  }

  // Add note to notes array
  const addNoteHandler = () => {
    // TODO: need to set up inline validation
    if (note.length === 0) {
      createToastMessage(
        <ToastMessage messageType='error' title='Operation failed' message={`Can not add empty note`} />,
      )
      return
    }
    addField(newNote)
    setIsDirty(true)
    cancelHandler()
  }

  // populate state with note to edit
  const addEditNote = (note: any, idx: number) => {
    dispatch({ type: 'edit', payload: { note, user, idx } })
    setIsDirty(true)
  }

  // pass edited note into editField
  const editNoteHandler = () => {
    // TODO: need to set up inline validation
    if (note.length === 0) {
      createToastMessage(
        <ToastMessage messageType='error' title='Operation failed' message={`Can not add empty note`} />,
      )
      return
    }
    editField(newNote, idx)
    setIsDirty(true)
    cancelHandler()
  }

  const addNoteFocusHandler = () => {
    dispatch({ type: 'field', fieldName: 'isDirty', payload: true })
  }

  useEffect(() => {
    if (isDirty && note.length === 0) {
      dispatch({ type: 'field', fieldName: 'isInvalid', payload: true })
    } else if (isDirty && note.length > 0) {
      dispatch({ type: 'field', fieldName: 'isInvalid', payload: false })
    }
  }, [isDirty, note])

  return (
    <>
      <Heading isSkeleton={isLoading} level={2}>
        Notes
      </Heading>
      <NoteEdit
        isLoading={isLoading}
        togglePublicHandler={togglePublicHandler}
        setValue={setNoteContentHandler}
        newNote={newNote}
        isEditing={isEditing}
        editNoteHandler={editNoteHandler}
        addNoteHandler={addNoteHandler}
        cancelHandler={cancelHandler}
        addNoteFocusHandler={addNoteFocusHandler}
        isDirty={isDirty}
        isInvalid={isInvalid}
        user={user}
      />
      <div>
        {notes.map((note, idx) => (
          <Note
            note={note}
            key={idx}
            isLoading={isLoading}
            editMode={true}
            idx={idx}
            removeField={removeField}
            addEditNote={(note, idx) => addEditNote(note, idx)}
          />
        ))}
      </div>
    </>
  )
}
